import { useState, useEffect, useCallback } from "react";
import { 
  ClipboardCheck, FileText, Download, MessageCircle, Mail, Phone,
  Building2, Calendar, Search, Filter, Plus, Eye, Trash2, FileDown,
  CheckCircle2, AlertTriangle, Clock, Users, DollarSign
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const axCfg = { withCredentials: true };

export default function DiagnosticosTab() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [userRole, setUserRole] = useState("admin");
  
  // Modales
  const [detalleOpen, setDetalleOpen] = useState(false);
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  
  // Prospecto seleccionado
  const [selectedProspect, setSelectedProspect] = useState(null);
  
  // Cotización
  const [serviceCatalog, setServiceCatalog] = useState([]);
  const [quoteForm, setQuoteForm] = useState({
    items: [],
    notes: "",
    validity_days: 7,
  });
  const [newItem, setNewItem] = useState({
    service_id: "",
    service_name: "",
    description: "",
    quantity: 1,
    unit_price: 0,
  });
  const [creatingQuote, setCreatingQuote] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [prospectsRes, meRes] = await Promise.all([
        // Obtener prospectos con diagnóstico enviado
        axios.get(`${API}/admin/diagnostic-requests/without-quotation`, axCfg),
        axios.get(`${API}/admin/me`, axCfg)
      ]);
      // Filtrar solo los que tienen diagnóstico enviado
      const diagnosticados = prospectsRes.data.filter(p => p.status === "diagnostic_sent");
      setData(diagnosticados);
      setUserRole(meRes.data?.role || "admin");
    } catch (e) {
      toast.error("Error al cargar diagnósticos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const fetchServiceCatalog = async () => {
    try {
      const res = await axios.get(`${API}/admin/service-catalog`, axCfg);
      setServiceCatalog(res.data);
    } catch (e) {
      console.error("Error loading service catalog");
    }
  };

  // Generar documento Word del diagnóstico
  const generarDocumentoDiagnostico = (prospect) => {
    // Crear contenido del documento
    const servicios = prospect.diagnostico_servicios || [];
    const observaciones = prospect.diagnostico_observaciones || "Sin observaciones";
    const siguienteAccion = prospect.diagnostico_siguiente_accion || "Por definir";
    
    const content = `
DIAGNÓSTICO REGULATORIO PETROCONTROL

================================================================================
DATOS DEL PROSPECTO
================================================================================

Empresa: ${prospect.company_name || "No especificada"}
Contacto: ${prospect.full_name}
Teléfono: ${prospect.phone || "No especificado"}
Email: ${prospect.email || "No especificado"}
Servicio Solicitado: ${prospect.service_type || "No especificado"}

================================================================================
RESULTADO DEL DIAGNÓSTICO
================================================================================

OBSERVACIONES Y RECOMENDACIONES:
${observaciones}

SERVICIOS RECOMENDADOS:
${servicios.length > 0 ? servicios.map(s => `• ${s}`).join("\n") : "• Pendiente de definir"}

================================================================================
SIGUIENTE PASO
================================================================================

${siguienteAccion}

================================================================================

Documento generado por PETROCONTROL
Fecha: ${new Date().toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })}

Para más información:
www.petrocontrol.mx
contacto@petrocontrol.mx
    `.trim();

    // Crear blob y descargar
    const blob = new Blob([content], { type: 'application/msword' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Diagnostico_${prospect.company_name || prospect.full_name}_${new Date().toISOString().split('T')[0]}.doc`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    toast.success("Documento de diagnóstico descargado");
  };

  // Abrir WhatsApp
  const sendWhatsApp = (prospect) => {
    const phone = (prospect.whatsapp || prospect.phone || "").replace(/[^0-9]/g, "");
    if (!phone) {
      toast.error("El prospecto no tiene número de teléfono");
      return;
    }
    const servicios = prospect.diagnostico_servicios || [];
    const serviciosTexto = servicios.length > 0 
      ? `\n\n*Servicios recomendados:*\n${servicios.map(s => `- ${s}`).join("\n")}`
      : "";
    
    const msg = `*PETROCONTROL - Seguimiento de Diagnóstico*\n\nEstimado/a ${prospect.full_name},\n\nDamos seguimiento al diagnóstico regulatorio realizado para ${prospect.company_name || "su empresa"}.${serviciosTexto}\n\n¿Podemos agendar una llamada para revisar la propuesta económica?\n\n_Equipo PETROCONTROL_`;
    
    window.open(`https://wa.me/52${phone}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  // Enviar email
  const sendEmail = (prospect) => {
    if (!prospect.email) {
      toast.error("El prospecto no tiene email");
      return;
    }
    const subject = encodeURIComponent(`PETROCONTROL - Propuesta de Servicios`);
    const body = encodeURIComponent(`Estimado/a ${prospect.full_name},\n\nDamos seguimiento al diagnóstico regulatorio realizado.\n\nAdjunto encontrará nuestra propuesta económica para los servicios recomendados.\n\nQuedamos a sus órdenes.\n\nSaludos cordiales,\nEquipo PETROCONTROL`);
    window.open(`mailto:${prospect.email}?subject=${subject}&body=${body}`, "_blank");
  };

  // Ver detalle
  const openDetalle = (prospect) => {
    setSelectedProspect(prospect);
    setDetalleOpen(true);
  };

  // Cotización
  const openQuoteModal = async (prospect) => {
    setSelectedProspect(prospect);
    setQuoteForm({ items: [], notes: "", validity_days: 7 });
    setNewItem({ service_id: "", service_name: "", description: "", quantity: 1, unit_price: 0 });
    await fetchServiceCatalog();
    setQuoteModalOpen(true);
  };

  const handleServiceSelect = (serviceId) => {
    const service = serviceCatalog.find(s => s.id === serviceId);
    if (service) {
      setNewItem({
        service_id: service.id,
        service_name: service.name,
        description: service.description,
        quantity: 1,
        unit_price: service.price,
      });
    }
  };

  const addItemToQuote = () => {
    if (!newItem.service_name || newItem.unit_price <= 0) {
      toast.error("Completa los datos del servicio");
      return;
    }
    setQuoteForm({
      ...quoteForm,
      items: [...quoteForm.items, { ...newItem }],
    });
    setNewItem({ service_id: "", service_name: "", description: "", quantity: 1, unit_price: 0 });
  };

  const removeItemFromQuote = (index) => {
    setQuoteForm({
      ...quoteForm,
      items: quoteForm.items.filter((_, i) => i !== index),
    });
  };

  const calculateTotals = () => {
    const subtotal = quoteForm.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const iva = subtotal * 0.16;
    const total = subtotal + iva;
    return { subtotal, iva, total };
  };

  const createQuotation = async () => {
    if (quoteForm.items.length === 0) {
      toast.error("Agrega al menos un servicio");
      return;
    }

    setCreatingQuote(true);
    try {
      await axios.post(`${API}/admin/quotations`, {
        prospect_id: selectedProspect.id,
        items: quoteForm.items,
        notes: quoteForm.notes,
        validity_days: quoteForm.validity_days,
      }, axCfg);
      
      toast.success("Cotización creada. El prospecto pasará a la pestaña Cotizaciones.");
      setQuoteModalOpen(false);
      setQuoteForm({ items: [], notes: "", validity_days: 7 });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear cotización");
    } finally {
      setCreatingQuote(false);
    }
  };

  // Filtrar datos
  const filteredData = data.filter(s => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      s.full_name?.toLowerCase().includes(searchLower) ||
      s.company_name?.toLowerCase().includes(searchLower) ||
      s.email?.toLowerCase().includes(searchLower)
    );
  });

  const { subtotal, iva, total } = calculateTotals();

  if (loading) return <p className="text-center py-12 text-gray-500">Cargando diagnósticos...</p>;

  return (
    <div className="space-y-4" data-testid="diagnosticos-tab">
      {/* Header con métricas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-none shadow-sm">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center">
                <ClipboardCheck className="h-4 w-4 text-white" />
              </div>
              <div>
                <p className="text-xl font-bold text-[#152238]">{data.length}</p>
                <p className="text-xs text-gray-600">Diagnósticos Enviados</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-none shadow-sm">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-violet-500 flex items-center justify-center">
                <FileText className="h-4 w-4 text-white" />
              </div>
              <div>
                <p className="text-xl font-bold text-[#152238]">{data.filter(d => d.diagnostico_servicios?.length > 0).length}</p>
                <p className="text-xs text-gray-600">Con Servicios</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-none shadow-sm">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-green-500 flex items-center justify-center">
                <DollarSign className="h-4 w-4 text-white" />
              </div>
              <div>
                <p className="text-xl font-bold text-[#152238]">{data.filter(d => d.diagnostico_completado).length}</p>
                <p className="text-xs text-gray-600">Listos para Cotizar</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-none shadow-sm">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
                <Clock className="h-4 w-4 text-white" />
              </div>
              <div>
                <p className="text-xl font-bold text-[#152238]">{data.filter(d => !d.diagnostico_completado).length}</p>
                <p className="text-xs text-gray-600">Pendientes</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 bg-white rounded-xl p-3 shadow-sm">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Buscar por nombre, empresa..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>
        <span className="text-sm text-gray-500">{filteredData.length} diagnósticos</span>
      </div>

      {/* Lista de diagnósticos */}
      {filteredData.length === 0 ? (
        <Card className="border-none shadow-sm">
          <CardContent className="text-center py-16">
            <ClipboardCheck className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No hay diagnósticos enviados</p>
            <p className="text-sm text-gray-400 mt-1">Los prospectos aparecerán aquí cuando se les envíe un diagnóstico</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {filteredData.map((prospect) => (
            <Card key={prospect.id} className="border-none shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="font-semibold text-gray-900">{prospect.full_name}</h3>
                      <Badge className="bg-amber-100 text-amber-700">Diagnóstico Enviado</Badge>
                      {prospect.diagnostico_completado && (
                        <Badge className="bg-green-100 text-green-700">Completo</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-600 mb-2 flex-wrap">
                      <span className="flex items-center gap-1">
                        <Building2 className="h-3 w-3" />
                        {prospect.company_name || "Sin empresa"}
                      </span>
                      <span className="font-medium text-[#103F38]">{prospect.service_type}</span>
                    </div>
                    
                    {/* Servicios recomendados */}
                    {prospect.diagnostico_servicios?.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-gray-500 mb-1">Servicios recomendados:</p>
                        <div className="flex flex-wrap gap-1">
                          {prospect.diagnostico_servicios.slice(0, 3).map((servicio, idx) => (
                            <Badge key={idx} variant="secondary" className="text-[10px]">
                              {servicio}
                            </Badge>
                          ))}
                          {prospect.diagnostico_servicios.length > 3 && (
                            <Badge variant="secondary" className="text-[10px]">
                              +{prospect.diagnostico_servicios.length - 3} más
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-4 text-xs text-gray-500 mt-2 flex-wrap">
                      <span className="flex items-center gap-1">
                        <Mail className="h-3 w-3" />{prospect.email}
                      </span>
                      {prospect.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="h-3 w-3" />{prospect.phone}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {new Date(prospect.created_at).toLocaleDateString('es-MX')}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2 flex-shrink-0">
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => generarDocumentoDiagnostico(prospect)}
                        title="Descargar diagnóstico en .DOC"
                      >
                        <Download className="h-4 w-4 mr-1" />
                        .DOC
                      </Button>
                      <Button
                        size="sm"
                        className="bg-[#103F38] hover:bg-[#0c332d]"
                        onClick={() => openQuoteModal(prospect)}
                      >
                        <FileText className="h-4 w-4 mr-1" />
                        Cotizar
                      </Button>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-green-600 border-green-300 hover:bg-green-50"
                        onClick={() => sendWhatsApp(prospect)}
                        title="Contactar por WhatsApp"
                      >
                        <MessageCircle className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => sendEmail(prospect)}
                        title="Enviar email"
                      >
                        <Mail className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openDetalle(prospect)}
                        title="Ver detalle"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Sheet de Detalle */}
      <Sheet open={detalleOpen} onOpenChange={setDetalleOpen}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5 text-amber-600" />
              Detalle del Diagnóstico
            </SheetTitle>
            <SheetDescription>
              {selectedProspect?.full_name} | {selectedProspect?.company_name}
            </SheetDescription>
          </SheetHeader>
          
          {selectedProspect && (
            <div className="py-6 space-y-6">
              {/* Info general */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Email:</span>
                  <p className="font-medium">{selectedProspect.email || "-"}</p>
                </div>
                <div>
                  <span className="text-gray-500">Teléfono:</span>
                  <p className="font-medium">{selectedProspect.phone || "-"}</p>
                </div>
                <div>
                  <span className="text-gray-500">Servicio:</span>
                  <p className="font-medium text-[#103F38]">{selectedProspect.service_type}</p>
                </div>
                <div>
                  <span className="text-gray-500">Fecha:</span>
                  <p className="font-medium">{new Date(selectedProspect.created_at).toLocaleDateString('es-MX')}</p>
                </div>
              </div>
              
              {/* Servicios recomendados */}
              {selectedProspect.diagnostico_servicios?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-sm text-gray-700 mb-2">Servicios Recomendados</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedProspect.diagnostico_servicios.map((servicio, idx) => (
                      <Badge key={idx} variant="secondary">{servicio}</Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Observaciones */}
              {selectedProspect.diagnostico_observaciones && (
                <div>
                  <h4 className="font-semibold text-sm text-gray-700 mb-2">Observaciones</h4>
                  <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                    {selectedProspect.diagnostico_observaciones}
                  </p>
                </div>
              )}
              
              {/* Siguiente acción */}
              {selectedProspect.diagnostico_siguiente_accion && (
                <div>
                  <h4 className="font-semibold text-sm text-gray-700 mb-2">Siguiente Acción</h4>
                  <p className="text-sm text-gray-600 bg-amber-50 p-3 rounded-lg border border-amber-200">
                    {selectedProspect.diagnostico_siguiente_accion}
                  </p>
                </div>
              )}
              
              {/* Acciones */}
              <div className="flex flex-wrap gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => generarDocumentoDiagnostico(selectedProspect)}>
                  <Download className="h-4 w-4 mr-2" />
                  Descargar .DOC
                </Button>
                <Button className="bg-[#103F38] hover:bg-[#0c332d]" onClick={() => { setDetalleOpen(false); openQuoteModal(selectedProspect); }}>
                  <FileText className="h-4 w-4 mr-2" />
                  Crear Cotización
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Modal Cotización */}
      <Dialog open={quoteModalOpen} onOpenChange={setQuoteModalOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-[#103F38]" />
              Nueva Cotización
            </DialogTitle>
            <DialogDescription>
              {selectedProspect?.full_name} | {selectedProspect?.company_name} | {selectedProspect?.email}
            </DialogDescription>
          </DialogHeader>

          {/* Add Service Section */}
          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            <h4 className="font-medium text-sm mb-3">Agregar Servicio</h4>
            
            {/* Servicios recomendados del diagnóstico */}
            {selectedProspect?.diagnostico_servicios?.length > 0 && (
              <div className="mb-3 p-2 bg-amber-50 rounded border border-amber-200">
                <p className="text-xs text-amber-700 mb-2">Servicios recomendados en el diagnóstico:</p>
                <div className="flex flex-wrap gap-1">
                  {selectedProspect.diagnostico_servicios.map((servicio, idx) => (
                    <Badge key={idx} variant="outline" className="text-xs cursor-pointer hover:bg-amber-100"
                      onClick={() => setNewItem({ ...newItem, service_name: servicio })}
                    >
                      + {servicio}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <div>
                <Label className="text-xs">Servicio del Catálogo</Label>
                <Select value={newItem.service_id} onValueChange={handleServiceSelect}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Seleccionar servicio..." />
                  </SelectTrigger>
                  <SelectContent>
                    {serviceCatalog.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name} - ${s.price?.toLocaleString('es-MX')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">O escribe manualmente</Label>
                <Input
                  className="mt-1"
                  placeholder="Nombre del servicio"
                  value={newItem.service_name}
                  onChange={(e) => setNewItem({ ...newItem, service_name: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div>
                <Label className="text-xs">Cantidad</Label>
                <Input
                  type="number"
                  min="1"
                  className="mt-1"
                  value={newItem.quantity}
                  onChange={(e) => setNewItem({ ...newItem, quantity: parseInt(e.target.value) || 1 })}
                />
              </div>
              <div>
                <Label className="text-xs">Precio Unitario</Label>
                <Input
                  type="number"
                  min="0"
                  className="mt-1"
                  value={newItem.unit_price}
                  onChange={(e) => setNewItem({ ...newItem, unit_price: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="flex items-end">
                <Button type="button" onClick={addItemToQuote} className="w-full bg-[#103F38] hover:bg-[#0c332d]">
                  <Plus className="h-4 w-4 mr-1" /> Agregar
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-xs">Descripción (opcional)</Label>
              <Input
                className="mt-1"
                placeholder="Descripción del servicio..."
                value={newItem.description}
                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
              />
            </div>
          </div>

          {/* Items List */}
          {quoteForm.items.length > 0 && (
            <div className="mb-4">
              <Label className="text-xs mb-2 block">Servicios en la cotización</Label>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="p-2 text-left">Servicio</th>
                      <th className="p-2 text-center">Cant.</th>
                      <th className="p-2 text-right">P.Unit</th>
                      <th className="p-2 text-right">Importe</th>
                      <th className="p-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {quoteForm.items.map((item, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="p-2">
                          <div className="font-medium">{item.service_name}</div>
                          {item.description && <div className="text-gray-500 text-[10px]">{item.description}</div>}
                        </td>
                        <td className="p-2 text-center">{item.quantity}</td>
                        <td className="p-2 text-right">${item.unit_price.toLocaleString('es-MX')}</td>
                        <td className="p-2 text-right font-medium">${(item.quantity * item.unit_price).toLocaleString('es-MX')}</td>
                        <td className="p-2">
                          <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={() => removeItemFromQuote(idx)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="mt-3 bg-[#103F38]/5 p-3 rounded-lg">
                <div className="flex justify-between text-sm mb-1">
                  <span>Subtotal:</span>
                  <span>${subtotal.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between text-sm mb-1">
                  <span>IVA (16%):</span>
                  <span>${iva.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between text-lg font-bold text-[#103F38]">
                  <span>TOTAL:</span>
                  <span>${total.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
            </div>
          )}

          {/* Notes and Validity */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <Label className="text-xs">Validez (días)</Label>
              <Select value={String(quoteForm.validity_days)} onValueChange={(v) => setQuoteForm({ ...quoteForm, validity_days: parseInt(v) })}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">3 días</SelectItem>
                  <SelectItem value="7">7 días</SelectItem>
                  <SelectItem value="15">15 días</SelectItem>
                  <SelectItem value="30">30 días</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Notas (opcional)</Label>
              <Textarea
                className="mt-1"
                rows={2}
                placeholder="Notas adicionales..."
                value={quoteForm.notes}
                onChange={(e) => setQuoteForm({ ...quoteForm, notes: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setQuoteModalOpen(false)}>
              Cancelar
            </Button>
            <Button
              className="bg-[#103F38] hover:bg-[#0c332d]"
              onClick={createQuotation}
              disabled={creatingQuote || quoteForm.items.length === 0}
            >
              {creatingQuote ? "Creando..." : "Crear Cotización"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
